import type { FormInstance } from 'antd';
import { Form, Switch } from 'antd';
import { useEffect } from 'react';
import { useStoreState, useStoreActions } from '../../../store';
import { DynamicLimitPercentageSettings } from '../../limit/DynamicLimitPercentageSettings';
import { CarrierServiceMethodDropDown } from '../../workflow/carrier/CarrierServiceMethodDropDown';

const { Item } = Form;

export const CostAdjustLimitsForm = ({
  form,
  targetCarrierLocked = false,
}: {
  form: FormInstance;
  // True when the simulation already has a linked CostAdjustConfig.
  // targetCarrier is create-only on the entity — the PUT path's configCommon
  // omits it, so a carrier change in edit mode would be silently dropped on
  // save. Lock the input in edit mode and surface a help-text note instead of
  // letting the user appear to edit it.
  targetCarrierLocked?: boolean;
}) => {
  const sessionPartnerId = useStoreState((state) => state.session.user?.sessionPartnerId);
  const fetchActivePartner = useStoreActions((actions) => actions.partner.fetchPartner);

  const costAdjustLimitEnabled = Form.useWatch('costAdjustLimitEnabled', form);

  useEffect(() => {
    if (sessionPartnerId) {
      fetchActivePartner(sessionPartnerId);
    }
  }, [sessionPartnerId, fetchActivePartner]);

  return (
    <>
      <div style={{ marginTop: 10 }}>
        <Item
          name="costAdjustLimitEnabled"
          extra={'Optionally enable cost adjustment limits to be applied to the simulation.'}
          label="Configure Shipment Limits"
        >
          <Switch checked={costAdjustLimitEnabled} />
        </Item>
      </div>
      {costAdjustLimitEnabled && (
        <div>
          <Item
            name="costAdjustLimitTargetCarrier"
            extra={
              targetCarrierLocked
                ? 'Carrier is locked once the limit is created. To use a different carrier, disable this limit and re-enable it with the new carrier.'
                : 'Carrier to apply the shipment limit to.'
            }
            label="Carrier"
            rules={[
              {
                required: true,
                message: 'Carrier is required.',
              },
            ]}
          >
            <CarrierServiceMethodDropDown
              singleSelect={true}
              hideServiceMethods={true}
              placeholder={'Select Carrier'}
              onlyConfiguredCarriersAndMethods={true}
              disabled={targetCarrierLocked}
            />
          </Item>
          <Item
            name="costAdjustLimitExcludedCarriers"
            extra={'Optional list of carriers to be excluded from limit calculations.'}
            label="Excluded Carriers"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const selectedCarrier = getFieldValue('costAdjustLimitTargetCarrier');
                  if (value && value.includes(selectedCarrier)) {
                    return Promise.reject(new Error('Excluded carriers cannot include the selected carrier.'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <CarrierServiceMethodDropDown
              singleSelect={false}
              hideServiceMethods={true}
              onlyConfiguredCarriersAndMethods={true}
            />
          </Item>
          <DynamicLimitPercentageSettings form={form} />
        </div>
      )}
    </>
  );
};
